import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { formatFxRate, formatQuantity, hoy, sumarDias, type Centavos, type Milli } from '@abastece/shared';
import type { Compra, Producto, Proveedor } from '@abastece/core';
import { db } from '../db/dexie.js';
import { dineroDeAnalisis, type DineroDeAnalisis } from '../lib/analisis.js';
import { useMonedaDeLosLibros } from '../lib/libros.js';
import {
  COBERTURAS,
  DIAS_URGENTE,
  VENTANA_DIAS,
  agruparPorCategoria,
  agruparPorProveedor,
  comprasDelProducto,
  resumenReponer,
  seEstaAcabando,
  sugerirCompras,
  ultimaCompraPorProveedor,
  type Cobertura,
  type GrupoReponer,
  type Sugerencia,
} from '../lib/reponer.js';
import { usePuede } from '../lib/sesion.js';
import { Pagina, Vacio } from '../ui/Pagina.js';
import { ElegirMoneda } from '../ui/ElegirMoneda.js';
import { Ico } from '../ui/Iconos.js';
import { Miniatura } from '../ui/Miniatura.js';
import { Modal } from '../ui/Modal.js';
import { diaDeLista } from '../ui/FilasDeDinero.js';
import { useT } from '../i18n/index.js';
import { cantidadCorta, etiquetaCategoriaProducto, unidadCorta } from '../i18n/dominio.js';
import { useNombreProducto } from '../i18n/productos.js';

/** What `sugerirCompras` needs from the tables: the window's sales, and everything else whole. */
export async function leerPorComprar() {
  const [productos, existencias, ventas, compras, reempaques, conteos] = await Promise.all([
    db.productos.toArray(),
    db.existencias.toArray(),
    db.ventas.where('fecha').aboveOrEqual(sumarDias(hoy(), -(VENTANA_DIAS - 1))).toArray(),
    db.compras.toArray(),
    db.reempaques.toArray(),
    db.conteos.toArray(),
  ]);
  return { productos, existencias, ventas, compras, reempaques, conteos };
}

/** For the home screen: how many are out, at their minimum, or days from out. */
export async function contarAcabandose(): Promise<number> {
  // The count does not depend on the period: anything urgent is short of any of them.
  return sugerirCompras({ ...(await leerPorComprar()), hoy: hoy(), cobertura: 1 }).filter(seEstaAcabando).length;
}

const CLAVE_COBERTURA = 'abastece.cobertura';
const CLAVE_AGRUPAR = 'abastece.reponer.agrupar';

/** By supplier, for when a salesperson comes round; by shelf, for a trip to the market. */
type Agrupar = 'proveedor' | 'categoria';

function coberturaGuardada(): Cobertura {
  try {
    const n = Number(localStorage.getItem(CLAVE_COBERTURA));
    return (COBERTURAS as readonly number[]).includes(n) ? (n as Cobertura) : 2;
  } catch {
    return 2;
  }
}

/**
 * By category unless this phone picked by supplier (the owner, 2026-10-16: «i also think "By
 * Category" should probably be the default selection not "By supplier"»).
 */
function agruparGuardado(): Agrupar {
  try {
    return localStorage.getItem(CLAVE_AGRUPAR) === 'proveedor' ? 'proveedor' : 'categoria';
  } catch {
    return 'categoria';
  }
}

function recordar(clave: string, valor: string) {
  try {
    localStorage.setItem(clave, valor);
  } catch {
    // Remembering the view is a convenience; the default is a fine answer every time.
  }
}

/** What one cost, landed: «C$ 36.58 c/u», «C$ 23.75 / lb»; in dollars, under one to the tenth of a cent. */
function porUnidad(t: ReturnType<typeof useT>, dinero: DineroDeAnalisis, costo: Centavos, cantidad: Milli, unidadBase: string): string {
  const monto = dinero.porUnidad((costo * 1000) / cantidad);
  return unidadBase === 'unidad'
    ? t('reponer.cadaUno', { monto })
    : `${monto} / ${unidadCorta(unidadBase)}`;
}

/**
 * Por comprar: what is running out, as lists to have at hand when a supplier's salesperson comes
 * round, or to take to the market.
 *
 * Suggestions, not orders: the distributors take the order on their round, so nothing here sends
 * one (the owner, 2026-10-16, of the «Pedir por WhatsApp» and the boxes to type each quantity
 * that were here). A product is a row of two lines: what it is and how much to buy; how much there
 * is, how fast it goes, how long it lasts, and about what the suggestion costs. Every list says
 * over its rows what each of those is (`Columnas`). Tapping one says what the number comes from and
 * what it cost the last times it was bought.
 *
 * Córdobas or dollars for the owner (2026-10-16: «also give that page a cordobas/dollar toggle»),
 * the books' «C$ | US$» and the phone's one choice of it (`useMonedaDeLosLibros`), written as
 * «Análisis» writes money (`dineroDeAnalisis`): an estimate to the córdoba, or to the cent under a
 * hundred dollars; what one unit cost, with its centavos. A cashier, who receives the goods and
 * pays in córdobas, reads córdobas and has no switch, as on «Fiado».
 */
export function Reponer() {
  const t = useT();
  const puede = usePuede('recibir_compras');
  const enDolaresSePuede = usePuede('ver_reportes');
  const libros = useMonedaDeLosLibros();
  const moneda = enDolaresSePuede ? libros.moneda : 'NIO';
  const dinero = useMemo(() => dineroDeAnalisis(moneda, libros.tasa), [moneda, libros.tasa]);
  const datos = useLiveQuery(() => leerPorComprar(), []);
  const proveedores = useLiveQuery(() => db.proveedores.toArray(), [], []);
  const [cobertura, setCobertura] = useState<Cobertura>(coberturaGuardada);
  const [agrupar, setAgrupar] = useState<Agrupar>(agruparGuardado);
  const [abierto, setAbierto] = useState<string | null>(null);

  const sugerencias = useMemo(
    () => (datos ? sugerirCompras({ ...datos, hoy: hoy(), cobertura }) : []),
    [datos, cobertura],
  );
  const ultimas = useMemo(() => ultimaCompraPorProveedor(datos?.compras ?? []), [datos]);

  if (!puede) {
    return (
      <Pagina titulo={t('reponer.titulo')}>
        <Vacio mensaje={t('comun.esDelDueno')} />
      </Pagina>
    );
  }

  const grupos = agrupar === 'categoria' ? agruparPorCategoria(sugerencias) : agruparPorProveedor(sugerencias);
  const resumen = resumenReponer(sugerencias);
  const urgentes = sugerencias.filter((s) => s.urgente).length;
  const proveedorDe = new Map(proveedores.map((p) => [p.id, p]));
  const productoDe = new Map((datos?.productos ?? []).map((p) => [p.id, p]));
  // A product that left the list while it was open (bought on another phone) closes it.
  const abierta = sugerencias.find((s) => s.producto.id === abierto);

  const pestana = (activa: boolean) =>
    `flex-1 rounded-lg px-1 py-2 text-sm whitespace-nowrap transition ${
      activa ? 'bg-marca-600 font-medium text-white' : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'
    }`;

  return (
    <Pagina
      titulo={t('reponer.titulo')}
      subtitulo={
        sugerencias.length > 0
          ? urgentes > 0
            ? t('reponer.subtituloUrgentes', { n: sugerencias.length, urgentes })
            : t('reponer.subtitulo', { n: sugerencias.length })
          : undefined
      }
    >
      {/* The weeks, then how the list is grouped and the owner's currency: one row where they fit, the currency under them where they do not. */}
      <div className="mb-3 flex flex-wrap gap-2">
        <div className="flex w-full gap-1 rounded-xl bg-slate-800 p-1 sm:w-auto sm:flex-1" data-reponer-cobertura>
          {COBERTURAS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => {
                setCobertura(c);
                recordar(CLAVE_COBERTURA, String(c));
              }}
              data-sin-icono="pestana"
              className={pestana(cobertura === c)}
            >
              {t(c === 1 ? 'reponer.unaSemana' : 'reponer.semanas', { n: c })}
            </button>
          ))}
        </div>
        <div className="flex flex-1 gap-1 rounded-xl bg-slate-800 p-1" data-reponer-agrupar>
          {(['proveedor', 'categoria'] as const).map((a) => (
            <button
              key={a}
              type="button"
              onClick={() => {
                setAgrupar(a);
                recordar(CLAVE_AGRUPAR, a);
              }}
              data-sin-icono="pestana"
              data-agrupar={a}
              aria-pressed={agrupar === a}
              className={pestana(agrupar === a)}
            >
              {t(a === 'proveedor' ? 'reponer.porProveedor' : 'reponer.porCategoria')}
            </button>
          ))}
        </div>
        {enDolaresSePuede && <ElegirMoneda moneda={libros.moneda} onElegir={libros.elegir} />}
      </div>
      <p className="mb-3 text-xs text-slate-400">{t('reponer.explicacion', { n: cobertura })}</p>
      {moneda === 'USD' && (
        <p className="-mt-2 mb-3 text-xs text-slate-500" data-reponer-tasa>
          {t('analisis.enDolares', { tasa: formatFxRate(libros.tasa) })}
        </p>
      )}

      {datos === undefined ? null : grupos.length === 0 ? (
        <Vacio mensaje={t('reponer.nada')} icono="reponer" />
      ) : (
        <>
          {/* Three across where a six-figure amount fits in a third; on a phone the amount takes a line of its own. The first two with the dot of their colour: what the rows' dots mean. */}
          <div className="mb-3 grid grid-cols-2 gap-2 min-[480px]:grid-cols-3" data-reponer-resumen>
            <Cifra
              valor={String(resumen.agotados)}
              etiqueta={t('reponer.agotados')}
              tono={resumen.agotados > 0 ? 'text-rose-300' : ''}
              punto={PUNTO.agotado}
              dato="agotados"
            />
            <Cifra
              valor={String(resumen.pronto)}
              etiqueta={t('reponer.pronto', { n: DIAS_URGENTE })}
              tono={resumen.pronto > 0 ? 'text-amber-300' : ''}
              punto={PUNTO.urgente}
              dato="pronto"
            />
            <Cifra
              valor={dinero(resumen.estimado)}
              etiqueta={t('reponer.alUltimoCosto')}
              tono=""
              dato="estimado"
              className="col-span-2 flex items-baseline gap-2 min-[480px]:col-span-1 min-[480px]:block"
            />
          </div>
          {resumen.sinCosto > 0 && (
            <p className="-mt-1 mb-3 text-xs text-slate-500" data-reponer-sin-costo>
              {t('reponer.sinCosto', { n: resumen.sinCosto })}
            </p>
          )}
          <div className="escalonar flex flex-col gap-3">
            {grupos.map((g) => (
              <Grupo
                key={g.clave}
                grupo={g}
                proveedor={g.proveedorId ? proveedorDe.get(g.proveedorId) : undefined}
                ultima={g.proveedorId ? (ultimas.get(g.proveedorId) ?? null) : null}
                // By shelf, each row says whom it was bought from.
                proveedorDe={agrupar === 'categoria' ? proveedorDe : null}
                productoDe={productoDe}
                dinero={dinero}
                onAbrir={setAbierto}
              />
            ))}
          </div>
        </>
      )}

      {abierta && datos && (
        <DetalleSugerencia
          s={abierta}
          cobertura={cobertura}
          compras={datos.compras}
          proveedorDe={proveedorDe}
          productoDe={productoDe}
          dinero={dinero}
          onCerrar={() => setAbierto(null)}
        />
      )}
    </Pagina>
  );
}

/** The hurry a row's dot says, in the colour the summary's figures say it in. */
const PUNTO = { agotado: 'bg-rose-400', urgente: 'bg-amber-400', resto: 'bg-slate-600' } as const;

function Cifra({
  valor,
  etiqueta,
  tono,
  punto,
  dato,
  className = '',
}: {
  valor: string;
  etiqueta: string;
  tono: string;
  /** The dot of the rows this figure counts. */
  punto?: string;
  dato: string;
  className?: string;
}) {
  return (
    <div className={`rounded-xl bg-slate-900/60 px-3 py-2 ring-1 ring-white/5 ring-inset ${className}`} data-cifra={dato}>
      <p className={`tabular text-base font-semibold whitespace-nowrap sm:text-lg ${tono}`}>{valor}</p>
      <p className="text-xs leading-tight text-slate-400">
        {punto && <span aria-hidden="true" className={`mr-1.5 inline-block size-2 rounded-full align-middle ${punto}`} />}
        {etiqueta}
      </p>
    </div>
  );
}

function Grupo({
  grupo,
  proveedor,
  ultima,
  proveedorDe,
  productoDe,
  dinero,
  onAbrir,
}: {
  grupo: GrupoReponer;
  proveedor: Proveedor | undefined;
  /** The day this supplier last brought goods. */
  ultima: string | null;
  /** Set by shelf, when each row names its supplier. */
  proveedorDe: Map<string, Proveedor> | null;
  productoDe: Map<string, Producto>;
  dinero: DineroDeAnalisis;
  onAbrir: (productoId: string) => void;
}) {
  const t = useT();
  const nombre = useNombreProducto();

  const titulo =
    grupo.categoria !== null
      ? etiquetaCategoriaProducto(grupo.categoria)
      : grupo.clave === 'reempaque'
        ? t('reponer.seReempacan')
        : grupo.clave === 'ninguno'
          ? t('reponer.sinProveedor')
          : (proveedor?.nombre ?? t('gastos.proveedor'));
  const urgentes = grupo.sugerencias.filter((s) => s.urgente).length;
  const estimado = grupo.sugerencias.reduce((a, s) => a + (s.estimado ?? 0), 0) as Centavos;
  const cuenta = [
    t('reponer.nProductos', { n: grupo.sugerencias.length }),
    urgentes > 0 ? t('reponer.nUrgentes', { n: urgentes }) : null,
    estimado > 0 ? t('reponer.unos', { monto: dinero(estimado) }) : null,
    ultima ? t('reponer.ultimaCompra', { fecha: diaDeLista(ultima) }) : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const cabecera = (
    <div className="min-w-0">
      <h2 className="truncate font-semibold">{titulo}</h2>
      <p className="tabular text-xs text-slate-400" data-reponer-cuenta>
        {cuenta}
      </p>
    </div>
  );

  /** What else a row says: whom it was bought from, by shelf; what it is bagged from. */
  const aparte = (s: Sugerencia): string | null => {
    if (s.reempacaDe) {
      const origen = productoDe.get(s.reempacaDe);
      return origen ? t('reponer.de', { producto: nombre(origen) }) : null;
    }
    if (proveedorDe && s.proveedorId) return proveedorDe.get(s.proveedorId)?.nombre ?? null;
    return null;
  };

  return (
    <section className="tarjeta p-4" data-reponer-grupo={grupo.clave}>
      {proveedor ? (
        // Whom to call, what is owed them, what else they sold the shop: their page.
        <Link
          to="/proveedores/$proveedorId"
          params={{ proveedorId: proveedor.id }}
          className="-mx-2 -mt-1 mb-1 flex items-center justify-between gap-3 rounded-lg px-2 py-1 transition hover:bg-slate-800/60"
          data-reponer-proveedor={proveedor.id}
        >
          {cabecera}
          <Ico icono="adelante" className="size-4 shrink-0 text-slate-500" />
        </Link>
      ) : (
        <header className="mb-1 flex">{cabecera}</header>
      )}
      {grupo.clave === 'ninguno' && <p className="mb-1 text-xs text-slate-500">{t('reponer.sinProveedorAyuda')}</p>}

      <div className="-mx-2">
        <Columnas conProveedor={proveedorDe !== null} />
        <ul className="flex flex-col">
          {grupo.sugerencias.map((s) => (
            <Fila
              key={s.producto.id}
              s={s}
              nombre={nombre(s.producto)}
              aparte={aparte(s)}
              dinero={dinero}
              onAbrir={() => onAbrir(s.producto.id)}
            />
          ))}
        </ul>
      </div>

      {grupo.clave === 'reempaque' && (
        <Link to="/inventario/reempaque" className="btn-secundario mt-2 h-11 min-h-0 w-full text-sm">
          <Ico icono="inventario" />
          {t('reponer.reempacar')}
        </Link>
      )}
    </section>
  );
}

/** A column's name over the rows' first line, and over their second what that line says. */
function Columna({ arriba, abajo, className }: { arriba: string; abajo: string; className: string }) {
  return (
    <span className={className}>
      <span className="block font-semibold tracking-wider text-slate-400 uppercase">{arriba}</span>
      <span className="block text-slate-500">{abajo}</span>
    </span>
  );
}

/**
 * What each column of the rows under it is (the owner, 2026-10-16: «label the columns in each
 * category so i don't have to assume what they are»), in two lines as the rows are, each in the
 * row's own place and widths: the product, and how much there is, how long it lasts, how fast it
 * sells, and by shelf whom it was bought from; on a laptop the last purchase, the day and how much
 * at what; and how much to buy over about what it costs. A row's own words stay in it («Hay 2»,
 * «alcanza 3 días»), since what a row says varies: sold out it has no days to last.
 */
function Columnas({ conProveedor }: { conProveedor: boolean }) {
  const t = useT();
  return (
    <div className="flex items-end gap-3 border-b border-slate-800 px-2 pb-1.5 text-[11px] leading-snug" data-reponer-columnas>
      <span aria-hidden="true" className="w-2 shrink-0" />
      <Columna
        arriba={t('reponer.colProducto')}
        abajo={t(conProveedor ? 'reponer.colSituacionProveedor' : 'reponer.colSituacion')}
        className="min-w-0 flex-1"
      />
      <Columna arriba={t('reponer.colUltimaCompra')} abajo={t('reponer.colCantidadCosto')} className="hidden w-40 shrink-0 text-right sm:block" />
      <Columna arriba={t('reponer.colComprar')} abajo={t('reponer.colCosto')} className="shrink-0 text-right sm:w-20" />
    </div>
  );
}

/**
 * A product on the list, in two lines, under the list's `Columnas`: what it is and how much to buy;
 * how much there is, how long it lasts and how fast it goes, and about what the suggestion costs.
 * The dot is the hurry: rose out, amber days from out, the colours of the summary's figures. Where
 * there is room, a laptop's, the last purchase between them: the day, and how much at what each.
 * A dash where there is nothing to say: never bought, no last purchase and no cost.
 */
function Fila({
  s,
  nombre,
  aparte,
  dinero,
  onAbrir,
}: {
  s: Sugerencia;
  nombre: string;
  aparte: string | null;
  dinero: DineroDeAnalisis;
  onAbrir: () => void;
}) {
  const t = useT();
  const agotado = s.existencia <= 0;
  return (
    <li>
      <button
        type="button"
        data-sin-icono="fila"
        data-reponer-fila={s.producto.id}
        onClick={onAbrir}
        className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition hover:bg-slate-800/60"
      >
        <span
          aria-hidden="true"
          className={`size-2 shrink-0 rounded-full ${agotado ? PUNTO.agotado : s.urgente ? PUNTO.urgente : PUNTO.resto}`}
        />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm">{nombre}</span>
          <span
            className={`tabular block truncate text-xs ${agotado ? 'text-rose-300' : s.urgente ? 'text-amber-300' : 'text-slate-500'}`}
            data-reponer-detalle-fila
          >
            {/* One text, not a span of its own: what a narrow screen cuts off stays inside the row. */}
            {aparte ? `${detalle(t, s)} · ${aparte}` : detalle(t, s)}
          </span>
        </span>
        <span className="hidden w-40 shrink-0 text-right text-xs sm:block" data-reponer-ultima>
          {s.ultimaCompra ? (
            <>
              <span className="tabular block text-slate-400">{diaDeLista(s.ultimaCompra.fecha)}</span>
              <span className="tabular block text-slate-500">
                {cantidadCorta(s.ultimaCompra.cantidad, s.producto.unidadBase)} ·{' '}
                {porUnidad(t, dinero, s.ultimaCompra.costo, s.ultimaCompra.cantidad, s.producto.unidadBase)}
              </span>
            </>
          ) : (
            <span className="block text-slate-600">—</span>
          )}
        </span>
        <span className="shrink-0 text-right sm:w-20">
          <span className="tabular block text-sm font-semibold" data-reponer-cantidad>
            {s.cantidad > 0 ? cantidadCorta(s.cantidad, s.producto.unidadBase) : '—'}
          </span>
          {s.estimado !== null && s.estimado > 0 ? (
            <span className="tabular block text-xs text-slate-500" data-reponer-estimado>
              {dinero(s.estimado)}
            </span>
          ) : (
            <span className="block text-xs text-slate-600">—</span>
          )}
        </span>
      </button>
    </li>
  );
}

/**
 * One product, opened: the number and what it comes from, and the last three times it was bought,
 * from whom, how much and at what cost, landed. What the salesperson's price is held against.
 */
function DetalleSugerencia({
  s,
  cobertura,
  compras,
  proveedorDe,
  productoDe,
  dinero,
  onCerrar,
}: {
  s: Sugerencia;
  cobertura: Cobertura;
  compras: readonly Compra[];
  proveedorDe: Map<string, Proveedor>;
  productoDe: Map<string, Producto>;
  dinero: DineroDeAnalisis;
  onCerrar: () => void;
}) {
  const t = useT();
  const nombre = useNombreProducto();
  const unidad = s.producto.unidadBase;
  const anteriores = comprasDelProducto(s.producto.id, compras);
  const proveedor = s.proveedorId ? proveedorDe.get(s.proveedorId) : undefined;
  const origen = s.reempacaDe ? productoDe.get(s.reempacaDe) : undefined;

  const ritmo =
    s.porDia >= 1000
      ? t('reponer.alDia', { n: formatQuantity(Math.round(s.porDia) as Milli, 1) })
      : s.porDia > 0
        ? t('reponer.aLaSemana', { n: formatQuantity(Math.round(s.porDia * 7) as Milli, 1) })
        : t('reponer.sinVentas');
  const alcanza =
    s.existencia <= 0
      ? t('reponer.agotado')
      : s.dias === null
        ? '—'
        : s.dias < 1
          ? t('reponer.menosDeUnDia')
          : t('reponer.nDias', { n: Math.floor(s.dias) });
  return (
    <Modal
      titulo={nombre(s.producto)}
      onCerrar={onCerrar}
      pie={
        proveedor && (
          <Link
            to="/proveedores/$proveedorId"
            params={{ proveedorId: proveedor.id }}
            className="btn-secundario w-full text-sm"
            data-reponer-ver-proveedor
          >
            <Ico icono="proveedores" />
            {t('reponer.verProveedor', { proveedor: proveedor.nombre })}
          </Link>
        )
      }
    >
      <div className="flex flex-col gap-4" data-reponer-detalle={s.producto.id}>
        <div className="flex items-center gap-3">
          <Miniatura producto={s.producto} tamano="lg" />
          <div className="min-w-0">
            <p className="text-xs text-slate-400">{t('reponer.comprarPara', { n: cobertura })}</p>
            <p className="tabular text-2xl font-bold" data-reponer-detalle-cantidad>
              {s.cantidad > 0 ? cantidadCorta(s.cantidad, unidad) : '—'}
            </p>
            {s.estimado !== null && s.estimado > 0 && (
              <p className="tabular text-xs text-slate-400">{t('reponer.estimado', { monto: dinero(s.estimado) })}</p>
            )}
          </div>
        </div>

        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm" data-reponer-datos>
          <dt className="text-slate-400">{t('reponer.hayAhora')}</dt>
          <dd className={`tabular ${s.existencia <= 0 ? 'text-rose-300' : ''}`}>
            {s.existencia <= 0 ? t('reponer.agotado') : cantidadCorta(s.existencia, unidad)}
          </dd>
          <dt className="text-slate-400">{t('reponer.seVende')}</dt>
          {/* Since it came in, for one that came after the shop's first sale: what the pace is of. */}
          <dd className="tabular" data-reponer-ritmo>
            {s.medidoDesde ? `${ritmo} · ${t('reponer.desdeQueLlego', { fecha: diaDeLista(s.medidoDesde) })}` : ritmo}
          </dd>
          <dt className="text-slate-400">{t('reponer.alcanzaPara')}</dt>
          <dd className={`tabular ${s.existencia <= 0 ? 'text-rose-300' : s.urgente ? 'text-amber-300' : ''}`}>{alcanza}</dd>
          {s.minimo !== null && (
            <>
              <dt className="text-slate-400">{t('reponer.minimoEtiqueta')}</dt>
              <dd className="tabular">{cantidadCorta(s.minimo, unidad)}</dd>
            </>
          )}
          <dt className="text-slate-400">{t('reponer.ultimaVenta')}</dt>
          <dd className="tabular">{s.ultimaVenta ? diaDeLista(s.ultimaVenta) : t('reponer.ningunaVenta')}</dd>
        </dl>
        {s.porDia === 0 && s.minimo === null && s.cantidad > 0 && (
          <p className="-mt-2 text-xs text-slate-500">{t('reponer.comoLaUltima')}</p>
        )}

        {origen ? (
          <div className="flex flex-col gap-2">
            <p className="text-sm text-slate-300">{t('reponer.de', { producto: nombre(origen) })}</p>
            <Link to="/inventario/reempaque" className="btn-secundario h-11 min-h-0 text-sm">
              <Ico icono="inventario" />
              {t('reponer.reempacar')}
            </Link>
          </div>
        ) : null}

        <section data-reponer-compras>
          <h3 className="rotulo">{t('reponer.ultimasCompras')}</h3>
          {anteriores.length === 0 ? (
            <p className="text-sm text-slate-400">{t('reponer.nuncaComprado')}</p>
          ) : (
            <ul className="flex flex-col divide-y divide-slate-800">
              {anteriores.map((c, i) => (
                <li key={`${c.compraId}:${i}`} className="flex items-start justify-between gap-3 py-2 text-sm" data-reponer-compra={c.compraId}>
                  <span className="min-w-0">
                    <span className="block truncate">{proveedorDe.get(c.proveedorId)?.nombre ?? t('gastos.proveedor')}</span>
                    <span className="tabular block text-xs text-slate-500">
                      {diaDeLista(c.fecha)} · {porUnidad(t, dinero, c.costo, c.cantidad, unidad)}
                    </span>
                  </span>
                  <span className="tabular shrink-0 text-right">
                    <span className="block font-medium">{cantidadCorta(c.cantidad, unidad)}</span>
                    <span className="block text-xs text-slate-500">{dinero(c.costo)}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </Modal>
  );
}

/**
 * "Hay 3 · alcanza 1 día · 2 al día · mínimo 5", in as few words as are true. How long it lasts
 * before how fast it goes: a narrow screen cuts the end of the line, and the hurry is what to keep.
 */
function detalle(t: ReturnType<typeof useT>, s: Sugerencia): string {
  const partes: string[] = [];
  partes.push(
    s.existencia <= 0 ? t('reponer.agotado') : t('reponer.hay', { n: formatQuantity(s.existencia) }),
  );
  if (s.dias !== null && s.existencia > 0) {
    const dias = Math.floor(s.dias);
    partes.push(dias < 1 ? t('reponer.seAcabaHoy') : t('reponer.alcanza', { n: dias }));
  }
  if (s.porDia >= 1000) {
    partes.push(t('reponer.alDia', { n: formatQuantity(Math.round(s.porDia) as Milli, 1) }));
  } else if (s.porDia > 0) {
    partes.push(t('reponer.aLaSemana', { n: formatQuantity(Math.round(s.porDia * 7) as Milli, 1) }));
  }
  if (s.minimo !== null) partes.push(t('reponer.minimo', { n: formatQuantity(s.minimo) }));
  return partes.join(' · ');
}
